import { Trans, useLingui } from "@lingui/react/macro";
import { ChevronDown, ChevronUp, History, RotateCw } from "lucide-react";
import { observer } from "mobx-react-lite";
import { ReactNode, useEffect, useMemo, useState } from "react";

import { PermissionBit } from "../../../api";
import { ApiConnector } from "../../../api/api-connector";
import { QuickLookButton } from "../../../components/quick-look/quick-look-button";
import { Button } from "../../../components/ui-kit/base/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui-kit/base/tabs";
import { Tooltip } from "../../../components/ui-kit/base/tooltip";
import { Select } from "../../../components/ui-kit/fields/select";
import { ShortcutHint } from "../../../components/ui-kit/shortcut-hint";
import { hasAdminAccess, hasPermission } from "../../../constants/permissions";
import { HeartCoreCharts } from "../heartcore-charts/heartcore-charts";
import { formatChartDateTime } from "../heartcore-charts/heartcore-format";
import {
  clearHeartcoreReports,
  DEFAULT_HEARTCORE_RANGE,
  HeartcoreRange,
  latestHeartcoreReportFetch,
  prefetchHeartcoreReports,
} from "../heartcore-charts/use-heartcore-chart-data";
import { Patient } from "../patient";
import { MeasurementsRecency } from "./measurements-recency";

type TabId = "dri" | "lvfp" | "heartcore";

export const PatientProfileChartTabs = observer(
  function PatientProfileChartTabs(props: {
    patient: Patient;
    api: ApiConnector;
  }) {
    const { patient, api } = props;
    const { t } = useLingui();

    // Bumped by "Reload data" (after clearing the report cache) so every
    // mounted HeartCoreCharts refetches its report.
    const [reloadNonce, setReloadNonce] = useState(0);
    // Wall-clock ms of the newest report fetch, for the label next to the
    // Reload button. Read from the report cache after each prefetch, so a
    // remount within the cache TTL shows the true (older) fetch time.
    const [lastRefreshed, setLastRefreshed] = useState<number | null>(null);
    // How far back the charts ask for. The default keeps the report (and the
    // worker's job) to DEFAULT_HEARTCORE_RANGE months; "Load all data" widens
    // it to the patient's whole history, a separate and slower report the cache
    // keeps alongside this one.
    const [range, setRange] = useState<HeartcoreRange>(DEFAULT_HEARTCORE_RANGE);

    const canDri = hasPermission(api.permissions, PermissionBit.ACCESS_DRI);
    const canLvfp = hasPermission(api.permissions, PermissionBit.ACCESS_LVFP);
    const canHeartCore = hasPermission(
      api.permissions,
      PermissionBit.RUN_HEART_CORE_QUICKLOOK,
    );
    // Gates the AFib probability chart on the DRI tab. Not a tab of its own —
    // the report's `afib` section is already permission-filtered by the BE, so
    // this only has to keep the fallback shape (afib_probability riding on the
    // Heart rate plot) from leaking to callers without the bit.
    const canAfib = hasPermission(api.permissions, PermissionBit.AFIB);
    // The PPG table a DRI point links to lives under the admin-gated `/raw`.
    const canOpenPpgs = hasAdminAccess(api.permissions);

    // Same consent gate as the QuickLook buttons: mounting HeartCoreCharts
    // auto-submits the analysis task, so without consent the charts must not
    // mount at all. `leading` (the tab's QuickLook button + extras) renders in
    // the charts' control row, sharing the line with the zoom presets.
    const charts = (variant: TabId, leading?: ReactNode) =>
      patient.consentOk() ? (
        <HeartCoreCharts
          patientId={patient.patientId}
          variant={variant}
          events={patient.events ?? []}
          canAfib={canAfib}
          canOpenPpgs={canOpenPpgs}
          range={range}
          reloadNonce={reloadNonce}
          leading={leading}
        />
      ) : (
        <>
          {leading && (
            <div className="mb-3 flex flex-wrap items-center gap-3">
              {leading}
            </div>
          )}
          <p className="text-muted-foreground py-8 text-center">
            <Trans>Charts are unavailable — patient consent is missing.</Trans>
          </p>
        </>
      );

    const visibleTabs = useMemo(() => {
      const tabs: { id: TabId; label: string }[] = [];
      if (canDri) tabs.push({ id: "dri", label: "DRI" });
      if (canLvfp) tabs.push({ id: "lvfp", label: "LVFP" });
      if (canHeartCore) tabs.push({ id: "heartcore", label: "HeartCore" });
      return tabs;
    }, [canDri, canLvfp, canHeartCore]);

    // Prefetch the shared HeartCore Master report up front — every visible
    // tab reads from it, so waiting for the active tab's own mount would
    // otherwise stall the first visit. Re-runs on reloadNonce; consent-gated
    // for the same reason the charts are (it submits the task), and skipped
    // entirely with no visible tabs (a patient with none of the three
    // permission bits has nothing here to prefetch).
    const consentOk = patient.consentOk();
    useEffect(() => {
      if (!consentOk || visibleTabs.length === 0) return;
      prefetchHeartcoreReports(patient.patientId, range);
      // loadMasterReport registers the cache entry synchronously, so this
      // reflects the just-started fetch (or the older cached one on a remount).
      setLastRefreshed(latestHeartcoreReportFetch(patient.patientId, range));
    }, [patient.patientId, visibleTabs.length, consentOk, range, reloadNonce]);

    return (
      <div className="mt-2">
        {visibleTabs.length > 0 && (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
              {/* `null` means no self-measurement config, so there is no
                  definition of a full measurement to count against. */}
              {patient.heartCore?.num_full_measurements && (
                <MeasurementsRecency
                  windows={patient.heartCore.num_full_measurements}
                />
              )}
              {/* Refetches the chart report JSONs — pointless (and misleading)
                  when the consent gate below hides the charts themselves. */}
              {patient.consentOk() && (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      clearHeartcoreReports(patient.patientId);
                      setReloadNonce((n) => n + 1);
                    }}
                  >
                    <RotateCw className="size-4" />
                    <Trans>Reload data</Trans>
                  </Button>
                  {/* Toggle, not a one-way switch: the default-window report
                      stays cached, so going back to it is instant and needs no
                      reload. Both windows are kept side by side in the cache.
                      The wording tracks DEFAULT_HEARTCORE_RANGE rather than
                      naming a period the button no longer requests. */}
                  <Tooltip
                    content={
                      range === "all"
                        ? t`Showing all data available for this patient — click to go back to the last ${DEFAULT_HEARTCORE_RANGE} months`
                        : t`Load all data available for this patient`
                    }
                  >
                    <Button
                      size="sm"
                      aria-pressed={range === "all"}
                      onClick={() =>
                        setRange((r) =>
                          r === "all" ? DEFAULT_HEARTCORE_RANGE : "all",
                        )
                      }
                    >
                      <History className="size-4" />
                      <Trans>Load all data</Trans>
                    </Button>
                  </Tooltip>
                  {lastRefreshed !== null && (
                    <span className="text-muted-foreground text-xs italic">
                      <Trans>
                        Last refreshed: {formatChartDateTime(lastRefreshed)}
                      </Trans>
                    </span>
                  )}
                </>
              )}
              {/* Collapses the profile header above — its own toggle is hidden
                  on this page (see PatientProfileHeader.showCollapseToggle). */}
              <Button
                size="sm"
                variant="secondary"
                className="ml-auto"
                onClick={() => {
                  patient.headerCollapsed = !patient.headerCollapsed;
                }}
              >
                {patient.headerCollapsed ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronUp className="size-4" />
                )}
                {patient.headerCollapsed ? t`Expand` : t`Collapse`}
                <ShortcutHint>⌘I</ShortcutHint>
              </Button>
            </div>
            <Tabs defaultValue={visibleTabs[0].id}>
              <TabsList>
                {visibleTabs.map((tab) => (
                  <TabsTrigger key={tab.id} value={tab.id}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {canDri && patient.driHeartCore && (
                <TabsContent value="dri" className="pt-4">
                  {charts(
                    "dri",
                    <QuickLookButton
                      label={t`DRI HeartCore`}
                      resultLabel={t`View HeartCore`}
                      model={patient.driHeartCore}
                      disabled={!patient.consentOk()}
                    />,
                  )}
                </TabsContent>
              )}

              {canLvfp && patient.lvfpHeartCore && (
                <TabsContent value="lvfp" className="pt-4">
                  {charts(
                    "lvfp",
                    <QuickLookButton
                      label={t`LVFP HeartCore`}
                      resultLabel={t`View HeartCore`}
                      model={patient.lvfpHeartCore}
                      disabled={!patient.consentOk()}
                    />,
                  )}
                </TabsContent>
              )}

              {canHeartCore && patient.heartCoreReport && (
                <TabsContent value="heartcore" className="pt-4">
                  {charts(
                    "heartcore",
                    <>
                      <QuickLookButton
                        label={t`HeartCore Report`}
                        resultLabel={t`View Report`}
                        model={patient.heartCoreReport}
                        disabled={!patient.consentOk()}
                      />
                      <div className="flex items-center gap-2">
                        <span>
                          <Trans>Plot range:</Trans>
                        </span>
                        <Select
                          attr="hcReportPlot"
                          model={patient}
                          values={[3, 6, 9, 12]}
                          onChange={() => patient.initHeartCore()}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <span>
                          <Trans>Table range:</Trans>
                        </span>
                        <Select
                          attr="hcReportTable"
                          model={patient}
                          values={[3, 6, 9, 12]}
                          onChange={() => patient.initHeartCore()}
                        />
                      </div>
                    </>,
                  )}
                </TabsContent>
              )}
            </Tabs>
          </>
        )}
        {!canLvfp && (
          <p className="mt-3 text-xs text-muted-foreground">
            <Trans>
              First LVFP reading is available after 4 measurements without PPG
              quality errors.
            </Trans>
          </p>
        )}
      </div>
    );
  },
);
